import { useState } from "preact/hooks";
import { unknownToString } from "@dhau/lang-extras";
import { parseUploadedFile } from "@brickme/frontend-common/src/bitmap/image-parsing.ts";
import bitmapToSmallestSizeEncodedImage from "@brickme/frontend-common/src/bitmap/bitmap-to-smallest-size-encoded-image.ts";
import type { SourceImage } from "@brickme/frontend-common/src/source-image/source-image.ts";
import { useTranslator } from "@brickme/frontend-common/src/i18n/translation.tsx";
import type { FormValues } from "~/hooks/use-form.ts";
import useForm from "~/hooks/use-form.ts";
import FormErrors from "~/components/forms/form-errors.tsx";
import { maxResolution } from "~/model.ts";
import SimplePage from "~/components/simple-page.tsx";
import UploadImageField from "~/components/forms/upload-image-field.tsx";
import BestResultsModal from "./best-results-modal.tsx";

const bestResultsModalId = "best-results-modal";

type PendingImageState = { readonly type: "pending" };
type ParsingImageState = { readonly type: "parsing" };
type ErrorImageState = { readonly type: "error"; readonly message: string };

type UploadImageProps = {
	readonly onComplete: (sourceImage: SourceImage) => void;
	readonly onCancel: () => void;
};

function UploadImage({ onComplete, onCancel }: UploadImageProps) {
	const t = useTranslator();

	const [imageState, setImageState] = useState<
		PendingImageState | ParsingImageState | ErrorImageState
	>({ type: "pending" });

	async function onSelectFile(sourceImage: File | undefined) {
		if (sourceImage === undefined) {
			return;
		}

		setImageState({ type: "parsing" });
		try {
			// TODO: Do parsing in a worker since very heavy?
			// Weight of a new worker probably not worth the effort, unless we
			// have some sort of multi-purpose utility worker
			const { bitmap, originalEncodedImage, originalSize } =
				await parseUploadedFile(sourceImage, maxResolution);
			const smallest = await bitmapToSmallestSizeEncodedImage(
				bitmap,
				// We can only use the original buffer for this purpose if it's the same size, otherwise
				// we run into trouble
				bitmap.width === originalSize.width &&
					bitmap.height === originalSize.height
					? originalEncodedImage
					: undefined,
			);
			onComplete({
				bitmap,
				fileName: sourceImage.name,
				contentType: smallest.mimeType,
				content: smallest.buffer,
			});
			setImageState({ type: "pending" });
		} catch (e) {
			setImageState({ type: "error", message: unknownToString(e) });
		}
	}

	// Form submission
	const { formError, formRef, formProps } = useForm({
		onSubmit(values: FormValues) {
			const sourceImage = values.sourceImage;
			if (typeof sourceImage === "string") {
				return;
			}

			onSelectFile(sourceImage);
		},
	});

	return (
		<>
			<SimplePage onBackClick={onCancel} centerContent>
				<form {...formProps}>
					<UploadImageField
						name="sourceImage"
						label={t("Upload your image")}
						onDropFile={onSelectFile}
						onInputChange={() => {
							formRef.current?.requestSubmit();
						}}
						required
					/>
					<FormErrors
						errors={[
							formError,
							imageState.type === "error" ? imageState.message : undefined,
						]}
					/>
				</form>
			</SimplePage>
			<BestResultsModal id={bestResultsModalId} showModalOnMount />
		</>
	);
}

export default UploadImage;
